import { ApiError, ValidationApiError } from "@anna-money/anna-web-lib/api";
import type { BasketFormValues } from "helpers/forms/baseTypes";
import { type ConsentFormValues } from "helpers/forms/consentTypes";
import { type DirectorStepFormValues } from "helpers/forms/directorTypes";
import { extractBackendValidationError, isAddressNotVerifiedError } from "helpers/forms/errors";
import type { CHVerificationFormValues } from "helpers/forms/incorporation/chVerificationTypes";
import { type CompanyFormValues } from "helpers/forms/incorporation/companyTypes";
import { type ConfirmAppointmentsFormValues, type ParticularsFormValues } from "helpers/forms/particularsTypes";
import { loadLibPhoneNumber } from "helpers/phoneLib";
import { type AnalyticsManager } from "services/analytics/analyticsManager";
import type { BillingControllerStore } from "services/billingController/billingControllerStore";
import type { BusinessActivitiesStore } from "services/businessActivities/businessActivitiesStore";
import type { CheckoutBasketStore } from "services/checkoutBasket/gb/checkoutBasketStore";
import { type CompanyStore } from "services/company/companyStore";
import { type CompanyAddressStore } from "services/companyAddress/gb/companyAddressStore";
import type { CompanyBusinessActivitiesStore } from "services/companyBusinessActivities/gb/companyBusinessActivitiesStore";
import { type CompanyProductsStore } from "services/companyProducts/companyProductsStore";
import { type ConsentStore } from "services/consent/consentStore";
import { type DirectorEntity, type DirectorStore } from "services/director/directorStore";
import type { DirectorPoaStore } from "services/directorIdentity/directorPoaStore";
import type { FeatureStore } from "services/feature/featureStore";
import { type FormationExpressStore } from "services/formationExpress/formationExpressStore";
import { type IncorporationStore } from "services/incorporation/incorporationStore";
import { IncorporationStatus } from "services/incorporation/incorporationTypes";
import { type IssuesStore } from "services/issues/issuesStore";
import { type PartnerOffersStore } from "services/partnerOffers/partnerOffersStore";
import { type PaymentMethodsStore } from "services/paymentMethods/paymentMethodsStore";
import { type PricingPlanStore } from "services/pricingPlan/gb/pricingPlanStore";
import { BasketQuestion } from "services/questions/gb/basketConstants";
import { type NameExtensionFormValues } from "services/questions/gb/identityConstants";
import { type IdentityQuestionsStore } from "services/questions/gb/identityQuestionsStore";
import type { ParticularsEntity } from "services/questions/gb/particularsConstants";
import { DirectionType } from "services/registerForm/registerFormStepsStoreBase";
import { RegisterFormStoreBase } from "services/registerForm/registerFormStoreBase";
import { CHVerificationEntity, type CHVerificationStepStore } from "services/stepStore/gb/chVerificationStepStore";
import { type CompanyStepEntity, type CompanyStepStore } from "services/stepStore/gb/companyStepStore";
import { IdentityStepEntity, type IdentityStepStore } from "services/stepStore/gb/identityStepStore";
import type { InitStepEntity, InitStepStore } from "services/stepStore/gb/initStepStore";
import { type ParticularsStepStore } from "services/stepStore/gb/particularsStepStore";
import type { PricingStepStore } from "services/stepStore/gb/pricingStepStore";
import { type SumsubApplicationStore } from "services/sumsub/sumsubApplicationStore";
import type { UserStore } from "services/user/userStore";
import { FormError } from "types/errors";
import type { FormSubmitResult } from "types/form";
import { FormValidationError } from "types/form";
import { IncoroporationFormStep } from "./constants";
import { type IncorporationRegisterFormStepsStore } from "./incorporationRegisterFormStepsStore";

export class IncorporationRegisterFormStore extends RegisterFormStoreBase<IncoroporationFormStep> {
  constructor(
    private readonly _consentStore: ConsentStore,
    private readonly _directorStore: DirectorStore,
    private readonly _companyStore: CompanyStore,
    private readonly _companyAddressStore: CompanyAddressStore,
    private readonly _businessActivitiesStore: BusinessActivitiesStore,
    private readonly _companyBusinessActivitiesStore: CompanyBusinessActivitiesStore,
    private readonly _companyProductsStore: CompanyProductsStore,
    private readonly _particularsStepStore: ParticularsStepStore,
    private readonly _paymentMethodsStore: PaymentMethodsStore,
    private readonly _sumsubStore: SumsubApplicationStore,
    private readonly _formationExpressStore: FormationExpressStore,
    private readonly _pricingPlanStore: PricingPlanStore,
    protected readonly _userStore: UserStore,
    protected readonly _analyticsManager: AnalyticsManager,
    protected readonly _registerFormStepsStore: IncorporationRegisterFormStepsStore,
    protected readonly _incorporationStore: IncorporationStore,
    protected readonly _companyIssuesStore: IssuesStore,
    protected readonly _companyStepStore: CompanyStepStore,
    protected readonly _chVerificationStepStore: CHVerificationStepStore,
    protected readonly _identityStepStore: IdentityStepStore,
    protected readonly _initStepStore: InitStepStore,
    protected readonly _pricingStepStore: PricingStepStore,
    protected readonly _checkoutBasketStore: CheckoutBasketStore,
    private readonly _featureStore: FeatureStore,
    private readonly _billingControllerStore: BillingControllerStore,
    private readonly _identityQuestionsStore: IdentityQuestionsStore,
    private readonly _directorPoaStore: DirectorPoaStore,
    private readonly _partnerOffersStore: PartnerOffersStore,
  ) {
    super(_registerFormStepsStore, _userStore, _incorporationStore, _companyIssuesStore, _analyticsManager);
  }

  get hideBackButton(): boolean {
    const activeType = this._stepsStore.activeStep.type;

    // Hide back button on the steps that follow payment — CHARGE has either been completed or
    // skipped (free plan / introducer-free), so back navigation would be ambiguous.
    // Hide it on COMPLIANCE_WAIT too — the screen only moves on the poll result, there's nothing
    // to go back to.
    const followsPayment = activeType === this._registerFormStepsStore.stepAfterCheckout;
    if (
      activeType === IncoroporationFormStep.IDENTITY ||
      activeType === IncoroporationFormStep.COMPLIANCE_WAIT ||
      followsPayment
    ) {
      return true;
    }

    return super.getHideBackButton();
  }

  // A deferred application has not been sent to Companies House yet, so its last step is not "Complete".
  protected getHideTitle(): boolean {
    return (
      this._stepsStore.activeStep.type === IncoroporationFormStep.COMPLETE &&
      this._incorporationStore.incorporation?.status === IncorporationStatus.Deferred
    );
  }

  async goBack(): Promise<void> {
    // Combined checkout mobile: back from payment goes to cart view
    if (
      this._featureStore.hasFeature("formationOnePageCheckout") &&
      this._registerFormStepsStore.activeStep.type === IncoroporationFormStep.BASKET &&
      this._checkoutBasketStore.combinedCheckoutMobileStep === "payment"
    ) {
      this._analyticsManager.event("combined-checkout.back-to-cart");
      this._checkoutBasketStore.combinedCheckoutMobileStep = "cart";
      return;
    }
    await super.goBack();
  }

  protected async _init(): Promise<void> {
    this._analyticsManager.event("gb-form-init-started");

    await this._companyStore.init();

    await Promise.all([
      this._consentStore.init(),
      this._companyAddressStore.init(),
      this._businessActivitiesStore.init(),
      this._companyBusinessActivitiesStore.init(),
      this._companyProductsStore.init(),
      this._pricingPlanStore.init(),
      this._directorStore.init(),
      this._sumsubStore.init(),
      this._companyIssuesStore.init(),
      this._incorporationStore.init(),
      this._paymentMethodsStore.init(),
      this._formationExpressStore.init(),
      loadLibPhoneNumber(this._analyticsManager),
    ]);

    if (this._featureStore.hasFeature("formationPaymentStatus")) {
      await this._billingControllerStore.fetchPaymentStatus();

      this._analyticsManager.event("gb-form-payment-status-checked", {
        isPaymentCompleted: this._billingControllerStore.isPaymentCompleted,
      });
    }

    this._analyticsManager.event("gb-form-init-completed");
  }

  // Payment is settled when it's already completed or not required at all (free plan) —
  // in both cases the BASKET/CHARGE steps have nothing left to do.
  private get _isPaymentSettled(): boolean {
    return (
      this._featureStore.hasFeature("formationPaymentStatus") &&
      (this._billingControllerStore.isPaymentCompleted || !this._billingControllerStore.isPaymentRequired)
    );
  }

  // A stale deep link (e.g. ?register_form_step=BASKET after a payment redirect) must not
  // strand the user on a payment step they can't complete again — fall back to the relevant step.
  protected _canResumeToStepFromUrl(step: IncoroporationFormStep): boolean {
    const isPaymentStep = step === IncoroporationFormStep.BASKET || step === IncoroporationFormStep.CHARGE;
    if (isPaymentStep && this._isPaymentSettled) {
      this._analyticsManager.event("gb-form-url-step-skipped-payment-settled", { step });
      return false;
    }
    // COMPLIANCE_WAIT submits the incorporation as soon as the (pre-existing) compliance check
    // reports success — it must never be reached directly, only via COMPLIANCE_CALL confirming the
    // required call actually happened. Otherwise a customer could jump straight to this URL and
    // have the application submitted without ever placing the required call.
    if (step === IncoroporationFormStep.COMPLIANCE_WAIT && this._featureStore.hasFeature("formationComplianceCall")) {
      this._analyticsManager.event("gb-form-url-step-skipped-compliance-wait", { step });
      return false;
    }
    return true;
  }

  // CH verification is complete (SumSub ID check passed + personal code present for every director),
  // so the only thing left is the compliance call before the application is submitted.
  // Personal codes can be saved outside the form, so a code alone doesn't prove identity is done.
  private get _shouldResumeToComplianceCall(): boolean {
    return this._featureStore.hasFeature("formationComplianceCall") && this._hasAllPersonalCodes && this._isIdvPassed;
  }

  private get _hasAllPersonalCodes(): boolean {
    const { directors } = this._directorStore;
    return (
      directors.length > 0 &&
      directors.every((director) => director.chPersonalCode != null && director.chPersonalCode.trim().length > 0)
    );
  }

  private get _isIdvPassed(): boolean {
    return this._sumsubStore.sumsubApplication?.isPassedVerifications === true;
  }

  private get _hasNonRejectedIncorporation(): boolean {
    const { incorporation } = this._incorporationStore;
    return incorporation != null && incorporation.status !== IncorporationStatus.Rejected;
  }

  protected _getRelevantStep(): IncoroporationFormStep {
    const { address } = this._companyAddressStore;
    const { mainDirector } = this._directorStore;
    const { sumsubApplication } = this._sumsubStore;

    // Resume directly to the compliance call once CH verification is done. The application is only
    // submitted (incorporation created) once the wait step after the call reports a successful
    // compliance check, so until then there is no incorporation to land us on COMPLETE. If the call
    // is already done, the call step reads that on mount and moves straight on to the wait step.
    if (this._shouldResumeToComplianceCall) {
      this._analyticsManager.event("gb-form-resume-compliance-call");
      return IncoroporationFormStep.COMPLIANCE_CALL;
    }
    if (this._featureStore.hasFeature("formationComplianceCall") && this._hasAllPersonalCodes) {
      this._analyticsManager.event("gb-form-resume-compliance-call-skipped-idv-not-passed");
    }

    // Pick the resume step from how far the user has progressed and (when gated by
    // formationPaymentStatus) whether payment is already settled or not required.
    const hasShares = this._companyStore.company?.totalSharesNum != null;
    if (this._isPaymentSettled && (sumsubApplication || hasShares)) {
      // Paid already, or free plan — jump past BASKET/CHARGE to whatever follows (Partner offers, then IDENTITY).
      this._analyticsManager.event("gb-form-payment-already-completed-skip-charge");
      return this._registerFormStepsStore.stepAfterCheckout;
    }
    if ((sumsubApplication || hasShares) && this._checkoutBasketStore.isIntroducerFreeWithoutAddons) {
      this._analyticsManager.event("introducer-free-no-addons-skip-basket");
      return this._registerFormStepsStore.stepAfterCheckout;
    }
    if (sumsubApplication || hasShares) {
      // Completed shares step — ready for basket/payment
      return IncoroporationFormStep.BASKET;
    }
    if (address) {
      // Started company step but not done with shares yet
      return IncoroporationFormStep.COMPANY;
    }

    if (mainDirector) {
      return IncoroporationFormStep.DIRECTOR;
    }

    return IncoroporationFormStep.CONSENT;
  }

  async onSubmit(data?: unknown, entity?: unknown): Promise<FormSubmitResult> {
    const wasRejected = this._incorporationStore.incorporation?.status === IncorporationStatus.Rejected;
    const result = await this._updateStepData(data, entity);
    if (result) {
      return result;
    }
    this._analyticsManager.event("click-next", {
      title: this._registerFormStepsStore.activeStep.title,
      type: this._registerFormStepsStore.activeStep.type,
      stepId: this._registerFormStepsStore.activeStepTestId,
    });

    // In rejection flow, submit incorporation and skip directly to COMPLETE after CONFIRM
    if (
      this._registerFormStepsStore.activeStep.type === IncoroporationFormStep.CONFIRM &&
      this.isRejectedButAllIssuesResolved
    ) {
      await this._createIncorporation();
      this._analyticsManager.event("rejection_flow_incorporation_created_after_confirm");
      this._registerFormStepsStore.setStep(this._registerFormStepsStore.completeStep);
      return;
    }

    // Rejection-flow resubmission happens inside CH_VERIFICATION — the compliance call/wait steps
    // after it would submit the application a second time.
    if (
      this._registerFormStepsStore.activeStep.type === IncoroporationFormStep.CH_VERIFICATION &&
      wasRejected &&
      this._hasNonRejectedIncorporation
    ) {
      this._analyticsManager.event("rejection_flow_incorporation_created_skip_to_complete");
      this._registerFormStepsStore.setStep(this._registerFormStepsStore.completeStep);
      return;
    }

    // Combined checkout: payment already handled inside CombinedCheckout, skip CHARGE
    if (
      this._featureStore.hasFeature("formationOnePageCheckout") &&
      this._registerFormStepsStore.activeStep.type === IncoroporationFormStep.BASKET &&
      entity === BasketQuestion.Checkout
    ) {
      this._analyticsManager.event("combined-checkout-skip-charge");
      this._registerFormStepsStore.setStep(this._registerFormStepsStore.stepAfterCheckout);
      return;
    }

    // Skip CHARGE step when payment is not required (e.g. free plan)
    if (
      this._featureStore.hasFeature("formationPaymentStatus") &&
      this._registerFormStepsStore.activeStep.type === IncoroporationFormStep.BASKET &&
      entity === BasketQuestion.Checkout
    ) {
      await this._billingControllerStore.fetchPaymentStatus();
      if (!this._billingControllerStore.isPaymentRequired) {
        this._analyticsManager.event("free-plan-skip-charge");
        await this._billingControllerStore.charge();
        if (this._billingControllerStore.state === "error") {
          this._analyticsManager.event("free-plan-charge-failed");
          await this._registerFormStepsStore.goNextQuestion();
          return;
        }
        this._registerFormStepsStore.setStep(this._registerFormStepsStore.stepAfterCheckout);
        return;
      }
    }

    await this._registerFormStepsStore.goNextQuestion();
  }

  protected async _updateStepData(data?: unknown, entity?: unknown): Promise<FormSubmitResult> {
    try {
      switch (this._registerFormStepsStore.activeStep.type) {
        case IncoroporationFormStep.CONSENT:
          await this._consentStore.updateConsents(data as ConsentFormValues);
          break;
        case IncoroporationFormStep.BEFORE_START:
          await this._initStepStore.updateEntity(data as CompanyFormValues, entity as InitStepEntity);
          break;
        case IncoroporationFormStep.COMPANY:
          return await this._companyStepStore.updateEntity(data as CompanyFormValues, entity as CompanyStepEntity);
        case IncoroporationFormStep.DIRECTOR:
          return await this._directorStore.updateMainDirector(data as DirectorStepFormValues, entity as DirectorEntity);
        case IncoroporationFormStep.SHARES:
          await this._particularsStepStore.updateMainParticulars(
            data as ParticularsFormValues | ConfirmAppointmentsFormValues,
            entity as ParticularsEntity,
          );
          break;
        case IncoroporationFormStep.CH_VERIFICATION: {
          await this._chVerificationStepStore.updateEntity(
            data as CHVerificationFormValues,
            entity as CHVerificationEntity,
          );
          break;
        }
        case IncoroporationFormStep.PARTNER_OFFERS:
          await this._partnerOffersStore.submit();
          break;
        case IncoroporationFormStep.CHARGE:
          break;
        case IncoroporationFormStep.BASKET:
          if (entity === BasketQuestion.Pricing) {
            await this._pricingStepStore.updateEntity(data as BasketFormValues);
          } else {
            this._checkoutBasketStore.validateForm();
          }
          break;
        case IncoroporationFormStep.IDENTITY:
          await this._identityStepStore.updateEntity(data as NameExtensionFormValues, entity as IdentityStepEntity);

          break;
        case IncoroporationFormStep.COMPLIANCE_CALL:
          // Nothing to do here — the call is over (or wasn't needed), goNextQuestion just advances
          // to the wait step. The application is submitted once that step's own check succeeds.
          break;
        case IncoroporationFormStep.COMPLIANCE_WAIT:
          // Reached only once the post-call compliance check succeeds — submit the application now
          // (deferred out of CH_VERIFICATION). goNextQuestion then advances to COMPLETE.
          if (!this._hasNonRejectedIncorporation) {
            await this._createIncorporation();
          }
          break;
      }
    } catch (error) {
      let result: FormValidationError;
      if (error instanceof ValidationApiError) {
        result = extractBackendValidationError(error);
      } else if (error instanceof ApiError || error instanceof FormError) {
        result = new FormValidationError(error.message);
      } else {
        throw error;
      }

      const isChStep = this._registerFormStepsStore.activeStep.type === IncoroporationFormStep.CH_VERIFICATION;

      // A gate rejection at submission creation is self-serve: send the user back to the POA step.
      // Scoped by the CH-verification step (GB-only) + the specific gate error, so it fires only on a
      // real backend rejection — not gated on the feature flag, so a feature-load failure can't swallow
      // the recovery and dead-end an already-paid customer.
      if (isChStep && isAddressNotVerifiedError(result)) {
        await this._routeBackToPoaStep();
      } else if (isChStep) {
        // A CH-step validation error that isn't the known gate message means the backend copy may have
        // drifted from isAddressNotVerifiedError — surface it so the recovery path can be kept in sync.
        this._analyticsManager.event("gb_form_ch_validation_error_unmatched");
      }

      return result;
    }
  }

  private async _routeBackToPoaStep(): Promise<void> {
    // Reset the POA upload/verification state and reload the address before navigating: after
    // (passed POA → address edit → CH rejection) the cached success would otherwise self-skip the POA
    // question on the stale isPoaPassedChecks flag and dead-end the customer in a rejection loop.
    // Best-effort: the refresh does a network reload, so a transient failure must NOT abort the
    // route-back — navigation always runs, or the paid customer stays dead-ended at CH verification.
    try {
      await this._directorPoaStore.onAddressEdited();
    } catch (e) {
      console.error("Failed to refresh POA state during gate recovery", e);
      this._analyticsManager.event("gb-form-poa-recovery-refresh-failed", {
        error: e instanceof Error ? e.message : "unknown_error",
      });
    }
    this._registerFormStepsStore.setStep(IncoroporationFormStep.IDENTITY, DirectionType.BACKWARD);
    this._identityQuestionsStore.onAddressNotVerified();
    this._analyticsManager.event("gb-form-address-not-verified-routed-to-poa");
  }
}
